import request from '@/utils/request'

/**
 * 获取用户列表
 * @param {Object} params 查询参数
 * @param {number} [params.pageNum=1] 页码
 * @param {number} [params.pageSize=10] 每页条数
 * @param {string} [params.username] 用户名
 * @param {string} [params.realName] 真实姓名
 * @param {number} [params.status] 状态：1-正常，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       username: string,
 *       realName: string,
 *       email: string,
 *       phone: string,
 *       status: number,
 *       roles: Array<string>,
 *       createTime: string,
 *       updateTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的用户列表
 */
export function getUserList(params) {
  return request({
    url: '/user/list',
    method: 'get',
    params
  })
}

/**
 * 获取用户详情
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 用户详情
 */
export function getUserById(id) {
  return request({
    url: `/user/${id}`,
    method: 'get'
  })
}

/**
 * 创建用户
 * @param {Object} data 用户数据
 * @param {string} data.username 用户名，4-50个字符
 * @param {string} [data.password] 密码，6-100个字符
 * @param {string} [data.realName] 真实姓名
 * @param {string} [data.email] 电子邮箱
 * @param {string} [data.phone] 手机号码，格式：1[3-9]开头的11位数字
 * @param {number} [data.status=1] 状态：1-正常，0-禁用
 * @param {Array<string>} [data.roles] 角色列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的用户信息
 */
export function createUser(data) {
  return request({
    url: '/user',
    method: 'post',
    data
  })
}

/**
 * 更新用户
 * @param {number} id 用户ID
 * @param {Object} data 用户数据
 * @param {string} data.username 用户名，4-50个字符
 * @param {string} [data.password] 密码，6-100个字符
 * @param {string} [data.realName] 真实姓名
 * @param {string} [data.email] 电子邮箱
 * @param {string} [data.phone] 手机号码，格式：1[3-9]开头的11位数字
 * @param {number} [data.status] 状态：1-正常，0-禁用
 * @param {Array<string>} [data.roles] 角色列表
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     username: string,
 *     realName: string,
 *     email: string,
 *     phone: string,
 *     status: number,
 *     roles: Array<string>,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的用户信息
 */
export function updateUser(id, data) {
  return request({
    url: `/user/${id}`,
    method: 'put',
    data
  })
}

/**
 * 删除用户
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteUser(id) {
  return request({
    url: `/user/${id}`,
    method: 'delete'
  })
}

/**
 * 更新用户状态
 * @param {number} id 用户ID
 * @param {number} status 状态：1-启用，0-禁用
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 更新结果
 */
export function updateUserStatus(id, status) {
  return request({
    url: `/user/${id}/status`,
    method: 'put',
    params: { status }
  })
}

/**
 * 获取用户角色
 * @param {number} id 用户ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<string>
 * }>} 用户角色列表
 */
export function getUserRoles(id) {
  return request({
    url: `/user/${id}/roles`,
    method: 'get'
  })
}

/**
 * 分配用户角色
 * @param {number} id 用户ID
 * @param {Object} data 角色数据
 * @param {Array<string>} data.roleIds 角色ID列表
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 分配结果
 */
export function assignUserRoles(id, data) {
  return request({
    url: `/user/${id}/roles`,
    method: 'post',
    data
  })
}

/**
 * 获取所有角色
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     roleName: string,
 *     roleCode: string,
 *     description: string,
 *     status: number,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 所有角色列表
 */
export function getAllRoles() {
  return request({
    url: '/user/roles',
    method: 'get'
  })
} 